import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  FormProps,
  Input,
  InputNumber,
  Radio,
  Row,
  Tooltip,
  Typography
} from 'antd';
import { FormItemAssignmentCriteria } from 'components/professor_area/FormItemAssignmentCriteria';
import { FormItemAssignmentInstruction } from 'components/professor_area/FormItemAssignmentIntruction';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import { InfoCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { getEvaluationPeriod } from 'services/classroom/evaluations';
import { useEffect, useMemo } from 'react';
import type { Dayjs } from 'dayjs';
import type {
  IProfessorAreaEvaluationEdit,
  IProfessorAreaEvaluationShow
} from 'interfaces/professor_area/evaluations';

interface TabGeneralProps {
  formProps: FormProps<IProfessorAreaEvaluationEdit>;
  data: IProfessorAreaEvaluationShow;
  isLoading: boolean;
  onSubmit: () => void;
}

export const TabGeneral = ({
  formProps,
  data,
  isLoading,
  onSubmit
}: TabGeneralProps) => {
  const {
    dateRange: requiredDates,
    accessType: accessTypeValue,
    duration_days,
    start_after_days
  } = getEvaluationPeriod(data.requirements);
  const accessType = Form.useWatch('access_type', formProps.form);

  useEffect(() => {
    formProps.form?.setFieldValue('starts_at', requiredDates[0]);
    formProps.form?.setFieldValue('ends_at', requiredDates[1]);
    formProps.form?.setFieldValue('access_type', accessTypeValue);
    formProps.form?.setFieldValue('duration_days', duration_days);
    formProps.form?.setFieldValue('start_after_days', start_after_days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateRange = useMemo(() => {
    if (!requiredDates) {
      return [];
    }

    return [requiredDates[0] as Dayjs, requiredDates[1] as Dayjs];
  }, [requiredDates]);

  // Read the kind from the loaded record (stable, present on mount) rather than
  // watching the form — avoids the "undefined on first render" flicker/regression.
  // Weight only applies to regular: replacement inherits from the parent and
  // final has none (backend forbids weight on both), so the field must stay
  // hidden for them, otherwise the required rule blocks saving a substitutiva.
  const kind = data.kind;

  return (
    <Form
      {...formProps}
      form={formProps.form}
      layout="vertical"
      initialValues={{
        ...formProps.initialValues,
        starts_at: dateRange[0],
        ends_at: dateRange[1]
      }}
    >
      <Form.Item
        label="Nome da atividade"
        name="name"
        rules={[{ required: true, message: 'Campo obrigatório' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item label="Descrição" name={['evaluation', 'description']}>
        <Input.TextArea rows={2} disabled />
      </Form.Item>

      <Form.Item>
        <Row>
          <Col md={5} className="ant-form-item-label">
            <label htmlFor="kind">Modalidade</label>
          </Col>
          <Col md={19}>
            <Form.Item noStyle name={'kind'}>
              <Radio.Group
                options={[
                  { label: 'Atividade padrão', value: 'regular' },
                  {
                    label: 'Atividade de recuperação',
                    value: 'final'
                  }
                ]}
                optionType="button"
                buttonStyle="outline"
                disabled
              />
            </Form.Item>
            <Typography.Text className="block text-xs mt-2 text-neutral-500">
              Atividade de recuperação deve acontecer após o término de todas as
              outras atividades
            </Typography.Text>
          </Col>
        </Row>
      </Form.Item>

      {kind === 'regular' && (
        <FormItemCol
          label="Peso"
          name="weight"
          cols={[5, 19]}
          itemsCenter
          rules={[
            { required: true, message: 'Campo obrigatório' },
            {
              type: 'number',
              min: 0,
              max: 100,
              message: 'Informe um valor entre 0 e 100'
            }
          ]}
        >
          <InputNumber min={0} max={100} className="max-w-[80px]" />
        </FormItemCol>
      )}

      <FormItemCol
        label="Nota mínima (0-100)"
        name="passing_score"
        itemsCenter
        cols={[5, 19]}
        rules={[{ required: true, message: 'Campo obrigatório' }]}
      >
        <Input type="number" className="max-w-[80px]" min={0} max={100} />
      </FormItemCol>

      <FormItemCol
        label="Tentativas"
        name="max_attempts"
        itemsCenter
        cols={[5, 19]}
        rules={[{ required: true, message: 'Campo obrigatório' }]}
      >
        <Input type="number" className="max-w-[80px]" />
      </FormItemCol>

      <Divider />

      <fieldset>
        <Typography.Title className="align-middle gap-2 flex" level={5}>
          Período de acessos
          <Tooltip
            placement="rightBottom"
            title="O período de acesso de uma avaliação deve estar entre a data de início e fim da turma"
          >
            <InfoCircleOutlined />
          </Tooltip>
        </Typography.Title>

        <Row>
          <Col span={24} style={{ marginBottom: 8 }}>
            <Form.Item
              name="access_type"
              initialValue="fixed"
              help={
                accessType === 'fixed' || !accessType
                  ? 'O acesso ocorre em uma data de início específica, independente da data de matrícula do aluno'
                  : 'O acesso ocorre a partir de um número de dias após a matrícula do aluno'
              }
              style={{ marginBottom: 0 }}
            >
              <Radio.Group buttonStyle="outline" className="mb-2">
                <Radio.Button value="fixed">Data fixa</Radio.Button>
                <Radio.Button value="relative">Data relativa</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        {accessType === 'fixed' && (
          <>
            <FormItemCol
              label="Início"
              name="starts_at"
              cols={[5, 19]}
              gutter={[0, 0]}
              rules={[{ required: true, message: 'Informe a data de início' }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" />
            </FormItemCol>

            <FormItemCol
              label="Término"
              name="ends_at"
              cols={[5, 19]}
              gutter={[0, 0]}
              rules={[{ required: true, message: 'Informe a data de término' }]}
            >
              <DatePicker showTime format="DD/MM/YYYY HH:mm" />
            </FormItemCol>
          </>
        )}

        {accessType === 'relative' && (
          <>
            <FormItemCol
              label="Dias para início"
              name="start_after_days"
              rules={[{ required: true }]}
              cols={[5, 2, 17]}
              help="dias após a matrícula do aluno."
            >
              <InputNumber />
            </FormItemCol>
            <FormItemCol
              label="Duração (dias)"
              name="duration_days"
              rules={[{ required: true }]}
              cols={[5, 2, 17]}
              help="dias após o início da avaliação."
            >
              <InputNumber />
            </FormItemCol>
          </>
        )}
      </fieldset>

      <Divider />

      <fieldset>
        <Typography.Title level={4} className="!mb-8">
          Instruções e critérios
        </Typography.Title>

        <Typography.Title level={5} className="mb-4">
          Intruções
        </Typography.Title>

        <FormItemAssignmentInstruction
          fieldName={['evaluation', 'instructions']}
          disabled
        />

        <Divider />

        <Typography.Title level={5} className="mb-4">
          Critérios
        </Typography.Title>

        <FormItemAssignmentCriteria
          fieldName={['evaluation', 'criteria']}
          disabled
        />
      </fieldset>

      <div className="flex justify-end border-t -mx-6 px-6 pt-6">
        <Button
          type="primary"
          onClick={onSubmit}
          loading={isLoading}
          icon={<SaveOutlined />}
        >
          Salvar
        </Button>
      </div>
    </Form>
  );
};
